import {useEffect, useState} from "react";
import {
    LOCAL_DOCUMENTS_INIT_PAGE_SIZE,
    LOCAL_DOCUMENTS_INIT_SORT_ORDER,
    LocalDocumentContentResponse,
    LocalDocumentsResponse,
    LocalDocumentsSearchQueryParams,
    SORT_COLUMN_MAP
} from "../models/local-documents.models";
import {SortOrder} from "../components/common/ResponsiveTable";
import {localDocumentsService} from "../services/local-documents.service";
import {useNavigate} from "react-router-dom";
import {RouteHelpers} from "../config/routeConstants";

const EMPTY_RESPONSE: LocalDocumentsResponse = {
    totalElements: 0,
    totalPages: 0,
    empty: true,
    first: true,
    last: true,
    size: 0,
    content: [],
    number: 0,
    sort: { empty: true, sorted: false, unsorted: true },
    numberOfElements: 0,
    pageable: {
        offset: 0,
        sort: { empty: true, sorted: false, unsorted: true },
        pageSize: 0,
        paged: false,
        pageNumber: 0,
        unpaged: true,
    },
};

export const useDocumentsDigitization = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(LOCAL_DOCUMENTS_INIT_PAGE_SIZE);

    const [sortColumn, setSortColumn] = useState<keyof LocalDocumentContentResponse | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>(LOCAL_DOCUMENTS_INIT_SORT_ORDER);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<LocalDocumentsSearchQueryParams>({});

    const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(new Set());

    const [data, setData] = useState<LocalDocumentsResponse>(EMPTY_RESPONSE);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const hasSearchOrFilters = search || filters.status || filters.type || filters.dateFrom || filters.dateTo;

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const responseData = hasSearchOrFilters
                    ? await localDocumentsService.fetchDocumentsProcessedSearch({
                        fileName: search || undefined,
                        dateFrom: filters.dateFrom,
                        dateTo: filters.dateTo,
                        status: filters.status,
                        type: filters.type,
                        packageId: filters.packageId,
                        sortColumn: sortColumn ? SORT_COLUMN_MAP[sortColumn] : undefined,
                        sortOrder: sortColumn ? sortOrder : undefined,
                        pageNumber: page,
                        pageSize,
                    })
                    : await localDocumentsService.fetchDocuments({
                        pageNumber: page,
                        pageSize,
                        sortColumn: sortColumn ? SORT_COLUMN_MAP[sortColumn] : undefined,
                        sortOrder: sortColumn ? sortOrder : undefined,
                    });

                setData(responseData);
            } catch (err) {
                setError(err as Error);
                console.error('Error fetching data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        void fetchData();
    }, [page, pageSize, sortColumn, sortOrder, search, filters]);

    const onPageChange = (newPage: number) => {
        setPage(newPage);
        setSelectedDocumentIds(new Set());
    };

    const onPageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setPage(0); // reset to the first page
    };

    const onSortChange = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column as keyof LocalDocumentContentResponse);
            setSortOrder('asc');
        }
        setPage(0);
    };

    const onSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const onFiltersChange = (newFilters: LocalDocumentsSearchQueryParams) => {
        setFilters(newFilters);
        setPage(0);
    };

    // // checkboxes
    const onCheckboxToggleSelect = (localDocumentId: number) => {
        setSelectedDocumentIds(prev => {
            const checkedDocsSet = new Set(prev);
            checkedDocsSet.has(localDocumentId) ? checkedDocsSet.delete(localDocumentId) : checkedDocsSet.add(localDocumentId);
            return checkedDocsSet;
        });
    };

    const onCheckboxSelectAll = () => {
        setSelectedDocumentIds(prev => {
            const checkedDocsSet = new Set(prev);
            data.content.forEach(d => checkedDocsSet.add(d.localDocumentId));
            return checkedDocsSet;
        });
    };

    const onCheckboxDeselectAll = () => setSelectedDocumentIds(new Set());

    const selectedOnPage = data.content.filter(d => selectedDocumentIds.has(d.localDocumentId));
    const isAllOnPageSelected = data.content.length > 0 && selectedOnPage.length === data.content.length;
    const isSomeOnPageSelected = selectedOnPage.length > 0 && !isAllOnPageSelected;

    const navigateToDocument = (documentId: number) => {
        const params = new URLSearchParams();

        if (search.trim().length > 0)   params.set('fileName', search.trim());
        if (filters.dateFrom)           params.set('dateFrom', filters.dateFrom);
        if (filters.dateTo)             params.set('dateTo', filters.dateTo);
        if (filters.status)             params.set('status', filters.status);
        if (filters.type)               params.set('type', filters.type);
        if (filters.packageId)          params.set('packageId', filters.packageId);

        const mappedColumn = sortColumn ? SORT_COLUMN_MAP[sortColumn] : undefined;
        if (sortColumn && mappedColumn) {
            params.set('sortColumn', mappedColumn)
        }
        if (sortColumn && mappedColumn) params.set('sortOrder', sortOrder);

        navigate(RouteHelpers.digitizationDetails(documentId, params));
    };

    const exportFiles = async (): Promise<boolean> => {
        if (selectedDocumentIds.size === 0) return false;

        const docIds = Array.from(selectedDocumentIds);

        try {
            const response = await localDocumentsService.exportLocalDocumentsToCSV(docIds);
            const text = await response.text();

            const BOM = '\uFEFF';
            const blob = new Blob([BOM + text], { type: 'text/csv;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = "documents_export.csv";
            link.style.display = "none";

            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            console.error('Error exporting files:', error);
            return false;
        } finally {
            onCheckboxDeselectAll();
        }
    };

    return {
        data,
        isLoading,
        error,
        filters,
        page,
        pageSize,
        sortColumn,
        sortOrder,
        onPageChange,
        onPageSizeChange,
        onSortChange,
        onSearchChange,
        onFiltersChange,
        selectedDocumentIds,
        selectedOnPage,
        isAllOnPageSelected,
        isSomeOnPageSelected,
        onCheckboxToggleSelect,
        onCheckboxSelectAll,
        onCheckboxDeselectAll,
        navigateToDocument,
        exportFiles,
    };
};